/**
 * ONE workout card, view-shaped — the same card in all three places it renders: a weekday column of
 * the grid, a training tile of the sequence tape, and the day dock's own header echo.
 *
 * The 04.08 canon's caption over the tape's tile is the reason this is one module and not two:
 * **"Kafel bez zmian"** — the card keeps the weekly card's whole anatomy, warm-up block, main work
 * with markers, load module and footer. Two view models producing "the same" card is exactly how
 * they stop being the same, so there is one, and what differs between the two canvases is passed in.
 *
 * ── What the two canvases genuinely differ by, and it is only the MOVE ───────────────────────
 * A weekly card is moved to a WEEKDAY and a tape tile to a POSITION. Both are numbers, both are
 * dispatched through `moveCommands`, and neither the card component nor this module needs to know
 * which: the coordinate travels on the target and is opaque to everything between the host that
 * built it and the host that dispatches it. That is what lets `SessionCard` be one component with
 * one interface rather than a component with a mode flag in it.
 *
 * ── The D-badge is GONE from the card, on both canvases (13 September 2026) ──────────────────
 * The header carried a badge before the name — "D1" — and it survived the 07.09 week round on one
 * stated condition: the sequence tile had no header over it, so the badge was the only position
 * marker the card carried. The 07.09 sequence mock gives every column a "DZIEŃ n" head and draws no
 * badge inside the card, so the condition is gone and the field with it.
 *
 * The NUMBER has not gone anywhere, but each canvas words it for itself now: the sequence canvas
 * addresses a position as "T3" (`planBuilder.trainingNumber`), the week names the weekday, and
 * `planBuilder.dayNumber` words the week's own column head. What changed is that the card stopped
 * printing a coordinate its own column already states.
 *
 * ── The footer lost its subtitle, and then the whole footer ──────────────────────────────────
 * "serie · ciężary · RPE · progresja" went first: gone from both specimens, leaving the bold
 * "Programuj dzień" and an arrow. The programator mock then removed that strip too — the card BODY
 * opens the panel — so on 7 September 2026 the pointer path moved onto the body and the words moved
 * into the ⋯ as `programLabel`, which is the keyboard and menu path view ADR §12 requires.
 *
 * ── What the 07.09 programator handoff moved (`Builder planu — tydzień + programator`) ───────
 * Three things, and each is a MOVE rather than a removal:
 *   · the DURATION left the header's sub-line for the weekday column above the card, so `meta` is
 *     now the two counts alone. See `meta` for what that costs the tape.
 *   · the "PRACA GŁÓWNA · 5 ćw." block heading is not drawn; its count joined `meta` and its title
 *     stays as the main-work list's accessible name.
 *   · the warm-up block's bare name list became full ROWS, so both blocks of the card draw the
 *     same shape — a badge, a 40×30 thumbnail and a name.
 * The load module lost its "18 pkt" total; the handoff's footer is a label, a bar and a value.
 *
 * **A component below this reads only the view model.** No `t`, no `Intl`, no arithmetic, no
 * `draft.phases[i]`. If a component is deciding something, it belongs here.
 */

import type {
  DayCard,
  ExerciseBlock,
  ExerciseRow,
  MuscleDisplayGroup,
  MuscleLoad,
} from '~/entities/plan-draft';
import { TEXT_LIMITS } from '~/entities/plan-draft';

import { KEY, muscleLabel, toBars, type MuscleBarView, type Translate } from './builderCopy';

/**
 * What the exercise CATALOGUE knows about one movement — everything the row that draws it needs
 * and nothing else.
 *
 * One lookup rather than two callbacks (`groupOf` plus a `coverOf`), because it is one question:
 * the catalogue either answered for this exercise or it did not, and a row that has a colour but no
 * photograph and a row that has neither are the same degradation. `usePlanBuilder` is the one place
 * allowed to build it — entities of the same layer may not import each other, so `plan-draft` never
 * learns that `exercise-library` exists.
 *
 * **Both fields are `null` when the catalogue read failed or is still in flight**, and the card
 * still renders: the chip is absent, the thumbnail's box shows its ground, and no bar claims a
 * share it has no part in. The catalogue's failure is not the plan's failure.
 */
export interface ExerciseChrome {
  readonly group: MuscleDisplayGroup | null;
  /** The movement's cover photograph — `ExerciseListItem.coverImageUrl`, carried raw. */
  readonly coverUrl: string | null;
}

/** Exercise id → what the catalogue knows. Supplied by the page; never built inside `model/`. */
export type LookUpExercise = (exerciseId: number) => ExerciseChrome;

export interface ExerciseRowView {
  readonly key: string;
  /**
   * `1`, or `2a` / `2b` inside a bracketed position. `null` on a warm-up a degenerate group left
   * standing among the singles — it occupies no main-work position, so the card draws no badge for
   * it. See `ExerciseRow.ordinal`.
   */
  readonly ordinal: string | null;
  readonly name: string;
  /** The primary muscle chip. `null` when the library read does not know the exercise. */
  readonly muscle: { readonly group: MuscleDisplayGroup; readonly label: string } | null;
  /**
   * The 40×30 thumbnail's source: the movement's photograph, else its 3D poster frame. `null` when
   * the catalogue names neither — the card then draws the tinted placeholder the handoff draws,
   * never a broken image.
   */
  readonly coverUrl: string | null;
  /**
   * A warm-up bracketed into the main work. It renders in the warm-up's blue and carries no muscle
   * chip, because it contributes no muscle points — the chip would claim a share of a bar it is not
   * in. Only ever true inside a `miniGroup`.
   */
  readonly isWarmup: boolean;
}

export type BlockView =
  | {
      readonly kind: 'single';
      readonly key: string;
      readonly row: ExerciseRowView;
    }
  | {
      readonly kind: 'superset' | 'miniGroup';
      readonly key: string;
      readonly ordinal: string;
      /**
       * The frame's own label — "Superset", or "Rozgrzewka" on a mini-group — so the bracket reads
       * as one decision rather than as exercises that happen to sit together.
       */
      readonly label: string;
      readonly rows: readonly ExerciseRowView[];
    };

/**
 * The card's warm-up block.
 *
 * **The mock's block head reads "ROZGRZEWKA … 8 MIN" and this carries no duration. Deviation, with
 * its reason, established at the contract on 7 September 2026.** Nothing on the wire gives a
 * warm-up run a duration — the role sits on each exercise, so the run is not a wire entity and has
 * nothing to hang a figure on, and the only estimate there is is the whole SESSION's authored
 * `estimatedDurationMinutes`. Execution ADR §1 forbids deriving one from set counts, so the block
 * prints its names and nothing else and the gap is flagged rather than filled.
 *
 * The counting rule went the same way and for a different reason: see `plan-day-programming`'s
 * warm-up head, where it lives under an ⓘ now.
 */
export interface WarmupView {
  readonly title: string;
  /**
   * The letter every warm-up row's badge carries — "R" for `Rozgrzewka`, "W" for `Warm-up`.
   *
   * A key rather than a hardcoded glyph: it is the initial of a TRANSLATED word, so a locale that
   * does not call preparation "rozgrzewka" would draw a letter belonging to another language. It is
   * decorative on the row — the block's own uppercase heading two lines above says the word in full.
   */
  readonly badge: string;
  /**
   * The warm-up's own rows, with the same thumbnail and the same identity a main-work row carries.
   *
   * It was `readonly string[]` — names only, because the 02.08 canon printed a bare list. The 07.09
   * handoff draws each warm-up as a full row (a bordered `R` badge, a 40×30 thumbnail, the name), so
   * the rows carry the same view as the main work's and the card's two blocks are one shape twice.
   * `ordinal` is always `null` on these: a warm-up occupies no main-work position, and the `R` badge
   * is the block's own mark rather than a number.
   */
  readonly rows: readonly ExerciseRowView[];
}

/**
 * How many rows the load module RESERVES while the exercise catalogue is still in flight.
 *
 * Three, because the 07.09 programator mock draws exactly three under every one of its days, and
 * because it is the modal count of a real week: a push day attributes to chest, shoulders and arms,
 * a pull day to back and arms, an upper day to four.
 *
 * ── The honest limit of this reserve, measured rather than assumed ───────────────────────────
 * A day's rows are the DISPLAY BUCKETS its exercises attribute to, and that is exactly what the
 * catalogue read answers — so the count cannot be known before the answer arrives, and no reserve
 * can be right for every day. Measured at 1280x620 with per-request latency injected: the module is
 * 34px of chrome plus 17px per row, so a day's footer runs 51px (one group) to 136px (all six), and
 * the WEEK ROW takes the tallest card's total. With no reserve at all the row grew by the whole of
 * the tallest module. With this one it is level whenever the tallest card's day attributes to three
 * groups, and off by the difference otherwise.
 *
 * Closing the remainder needs a product decision the owner has not been asked for — a footer of a
 * FIXED height on every day (which the mock in fact draws, and which would have to scroll or drop
 * groups on a full-body day), or sourcing the split from `DayTemplateResponse.muscleSplit`, which
 * the plan read already carries and which would remove the wait altogether at the cost of stating
 * percentages under a heading that says sets. Flagged, not chosen here.
 */
export const RESERVED_LOAD_ROWS = 3;

export interface DayLoadView {
  readonly title: string;
  /** The day's groups. EMPTY exactly while `isPending` — never a fabricated placeholder row. */
  readonly rows: readonly MuscleBarView[];
  /**
   * The catalogue read is still open, so the module reserves its box and stands a skeleton in it
   * rather than appearing when the answer lands. `pending`, `error` and `empty` are three states:
   * this is the first, an answer with nothing in it draws NO module at all (see `toDayCardView`),
   * and neither is a branch on `rows.length`.
   */
  readonly isPending: boolean;
  /** How many skeleton rows stand in the reserve. See `RESERVED_LOAD_ROWS`. */
  readonly reservedRows: number;
  /** The word beside `aria-busy` while the wait lasts. `null` once the groups can speak. */
  readonly pendingLabel: string | null;
}

/**
 * One stop a card can be sent to — a weekday of the grid, or a position on the tape.
 *
 * The ONE entry of the ⋯ menu and the ONE stop of the keyboard path, so the two cannot offer
 * different sets (view ADR §12: the menu path is a full equivalent, not a reduced fallback).
 */
export interface MoveTargetView {
  /**
   * The coordinate the command takes — a weekday (1–7) on the grid, a 0-based position on the tape.
   *
   * **Opaque to everything between the host that built it and the host that dispatches it.** That is
   * what lets one card component serve two canvases: it carries the number back without ever
   * learning what it counts.
   */
  readonly coordinate: number;
  /** "Czwartek — wolne", "Środa — Pull A, zamiana", "Pozycja 3 — Nogi A". */
  readonly label: string;
  /**
   * Why this entry cannot act, or `null`. Only ever set on the card's OWN stop: it stays in the
   * menu, focusable, carrying the reason — removing it would leave a trainer counting entries to
   * work out where they are.
   */
  readonly disabledReason: string | null;
  /** What the live region says when the keyboard path steps onto this stop. */
  readonly preview: string;
}

/** Everything the card needs to be moved, whichever canvas it is standing on. */
export interface DayMoveView {
  /** Where this card sits among `targets` — the keyboard lift's origin. 0-based. */
  readonly index: number;
  /**
   * "Przestaw" — the context menu's entry that starts the keyboard lift. It was the grip's name,
   * "Przenieś trening Push A", until the owner removed the grip on 15 September 2026; the menu is
   * already scoped to one card, so the entry needs no name in it.
   */
  readonly liftEntryLabel: string;
  /** "Więcej działań dla treningu Push A" — the ⋯ trigger's name, so cards are told apart. */
  readonly menuLabel: string;
  /** "Przenieś na dzień…" / "Przenieś w cyklu…" — the group heading over the entries. */
  readonly menuGroupLabel: string;
  /**
   * What the live region says the moment a keyboard lift picks this card up — the name, where it
   * stands, and what the arrows now do. Said ONCE: repeating the instruction on every arrow press
   * would bury the position under it.
   */
  readonly liftedLabel: string;
  readonly targets: readonly MoveTargetView[];
  /**
   * Why THIS card cannot be moved, whatever the canvas thinks — `null` when it can.
   *
   * One cause today, and it is a real one the tape reaches: a phase holding a single training has
   * exactly one stop, so there is nowhere to go. `useKeyboardMove` already refuses a lift at
   * `count <= 1`, but it refused SILENTLY. The refusal is worded here so the menu's "Przestaw" can
   * stay focusable and say why, which is the same treatment every other closed control on this
   * screen takes (view ADR §12).
   */
  readonly disabledReason: string | null;
}

/** The accessible names of the card header's duplicate and delete icons, for one workout. */
export interface DayHeaderActionsView {
  readonly duplicate: string;
  readonly delete: string;
}

export interface DayCardView {
  readonly key: string;
  /**
   * The day's durable wire identity — the ADDRESS an edit names its target by.
   *
   * The same string as `key`, and deliberately a second field rather than a reuse of it. `key` is a
   * React reconciliation hint the screen is free to change; this is the coordinate
   * `renameSession` writes against. Conflating them means the day somebody changes the key to
   * something more convenient is the day renaming silently stops finding its target.
   */
  readonly slotToken: string;
  /** The SESSION's name, or the untitled fallback. What the rename affordance edits. */
  readonly title: string;
  /**
   * The session's name exactly as authored — `null` when the trainer never gave it one.
   *
   * `title` carries the fallback so a card always reads as something; an editor has to start from
   * what was actually typed, or opening the field would offer to save the placeholder as a real
   * name on the very first keystroke.
   */
  readonly authoredTitle: string | null;
  /** The rename field's label. The input is unlabelled otherwise: the `h3` around it is not a label. */
  readonly nameFieldLabel: string;
  /**
   * The longest session name the wire will accept — `PostSessionTemplateInput.Name`'s
   * `[MaxLength(120)]`, mirrored in `TEXT_LIMITS.sessionName`.
   *
   * It travels on the view model rather than being read from `entities/plan-draft` by the component,
   * because a component may not import a slice's caps table directly — the same rule that keeps every
   * label on this model. The field enforces it at the input, where the trainer sees the answer,
   * instead of letting a 121st character become a 400 after a save.
   */
  readonly nameMaxLength: number;
  /**
   * "5 ćw. · 16 serii" — the header's sub-line, under the workout's name.
   *
   * It was "~64 min · 14 serii". The 07.09 handoff moves the DURATION out of the card and onto the
   * weekday column's header, where it stands over the card rather than inside it, and puts the two
   * counts here instead — which is also where the "PRACA GŁÓWNA · 5 ćw." block heading used to
   * carry one of them. Both numbers are the projection's own: `mainExerciseCount` excludes a
   * bracketed warm-up and `countingSetCount` runs through `countsAsWork`, so a warm-up set is in
   * neither.
   *
   * The honest consequence, recorded rather than left to be discovered: the TAPE draws this same
   * card with no column header over it, so a self-paced tile no longer states its duration
   * anywhere. Restoring it is the tape's own header's job, not a second shape for this card.
   */
  readonly meta: string;
  readonly warmup: WarmupView | null;
  /** The main-work list's accessible name. The 07.09 handoff draws no visible block heading. */
  readonly mainTitle: string;
  readonly blocks: readonly BlockView[];
  /** `null` when the day trains nothing the library could attribute. */
  readonly load: DayLoadView | null;
  /**
   * "Programuj dzień" — the ⋯ menu's entry that opens the day panel.
   *
   * It was the label on a strip across the card's foot until 7 September 2026. The programator mock
   * draws no such control: the CARD BODY is what opens the panel. So the pointer path moved onto the
   * body and this word moved into the menu, which is where view ADR §12's keyboard-and-menu
   * equivalent for a pointer gesture belongs. The menu names the workout, so the entry does not.
   */
  readonly programLabel: string;
  /**
   * "Zmień nazwę treningu" — the card's CONTEXT-MENU entry for the rename.
   *
   * The 07.09 programator mock draws no rename affordance on the card at all, so the menu is the
   * only path and its wording has to stand on its own. `nameFieldLabel` names the FIELD once it is
   * open; this names the act that opens it.
   */
  readonly renameLabel: string;
  /**
   * The two icon buttons in the card header, named for THIS workout.
   *
   * They carry the day's name where the ⋯ menu's own entries do not, because the menu is already
   * named "Więcej działań dla treningu Push A" and its entries are read inside it. These two stand
   * bare in a header beside six identical pairs, so "Duplikuj trening" alone would be seven controls
   * a screen-reader user cannot tell apart — the same argument the grip and the ⋯ already make.
   *
   * The REFUSALS are not here: they are facts about the phase, not the day, and the canvas already
   * answers them once for every card (`DayActionsView`).
   */
  readonly headerActions: DayHeaderActionsView;
  /**
   * "bez ćwiczeń — nie zapisze się" — `null` on every day that holds work.
   *
   * A day carrying no exercise is DROPPED by `draftToRequest`, so it exists on the canvas and will
   * not exist after the next save. Nothing said so: "+ Trening" minted a tile that looked exactly
   * like the four beside it, and a trainer who added a day and walked away lost it silently. The
   * cycle's own numbers already exclude it (`TapeDay.countsToward`); this is the tile saying why it
   * is not in them.
   *
   * The canon draws no such caption, and it could not — it draws finished plans. Flagged as an
   * addition rather than a transcription.
   */
  readonly unsavedNote: string | null;
  /**
   * What an empty day says in the space its exercises would fill. `null` once it holds anything.
   *
   * A sentence rather than a control: the whole card already opens the day, so a button inside it
   * would be a second way to do what pressing anywhere here does.
   */
  readonly emptyInvitation: string | null;
  readonly move: DayMoveView;
}

function toExerciseRowView(
  t: Translate,
  row: ExerciseRow,
  lookUpExercise: LookUpExercise,
): ExerciseRowView {
  const known = lookUpExercise(row.exerciseId);
  // A warm-up carries no chip: it contributes nothing to the bars, so naming a group beside it
  // would point at a share it has no part in. It DOES carry its thumbnail — a photograph of the
  // movement is identity, not attribution.
  const group = row.isWarmup ? null : known.group;
  return {
    key: row.slotToken,
    ordinal: row.ordinal,
    name: row.name,
    muscle: group === null ? null : { group, label: muscleLabel(t, group) },
    coverUrl: known.coverUrl,
    isWarmup: row.isWarmup,
  };
}

function toBlockView(
  t: Translate,
  block: ExerciseBlock,
  lookUpExercise: LookUpExercise,
): BlockView {
  if (block.kind === 'single') {
    return {
      kind: 'single',
      key: block.exercise.slotToken,
      row: toExerciseRowView(t, block.exercise, lookUpExercise),
    };
  }
  return {
    kind: block.kind,
    key: block.members[0]?.slotToken ?? block.ordinal,
    ordinal: block.ordinal,
    // The mini-group borrows the day warm-up block's own word, because it IS a warm-up — the only
    // difference is that this one was bracketed to a specific lift instead of the whole session.
    label: t(`${KEY}.${block.kind === 'superset' ? 'superset' : 'warmup'}`),
    rows: block.members.map((member) => toExerciseRowView(t, member, lookUpExercise)),
  };
}

/** What the caller supplies that the card cannot see — its coordinate, and where it can be sent. */
export interface DayCardMoveInput {
  readonly index: number;
  /**
   * The training's 1-based position in the phase — what an unnamed day is called (`dayDisplayName`).
   *
   * Separate from `index`, which is the coordinate the MOVE speaks in and means something different
   * on each canvas: the tape counts positions, the week counts weekdays. Numbering a Friday card
   * "Dzień 5" under a column already headed "piątek" would name the column, not the training.
   */
  readonly position: number;
  readonly menuGroupLabel: string;
  /**
   * The card's lift announcement, worded by the canvas because only it knows what the position
   * counts. Takes the session's name, which is this module's to resolve (it owns the fallback).
   */
  readonly liftedLabel: (name: string) => string;
  readonly targets: readonly MoveTargetView[];
}

/**
 * What a training is called — what the trainer typed, or its number when they typed nothing.
 *
 * The fallback NUMBERS the day rather than calling it nameless (owner, 8 September 2026). A week of
 * unnamed trainings read as four identical "Trening bez nazwy" cards, which is the one thing a
 * fallback name must not do: it has to tell them apart. The number is the training's position in
 * the phase, which is the coordinate its move menu already speaks in.
 *
 * Exported because a card is named in three places at once — on itself, in a SIBLING's move menu,
 * and in the drop targets — and a card that reads "Dzień 2" while its neighbour offers to swap with
 * "Trening bez nazwy" is naming the same training twice, differently.
 */
export function dayDisplayName(t: Translate, title: string | null, position: number): string {
  return title ?? t(`${KEY}.untitledDay`, { n: position });
}

export function toDayCardView(
  t: Translate,
  locale: string,
  card: DayCard,
  load: MuscleLoad | undefined,
  lookUpExercise: LookUpExercise,
  move: DayCardMoveInput,
  /**
   * Whether the exercise CATALOGUE read is still open — `library.isPending` and nothing else, the
   * same flag the volume rail waits on. It reaches the load module and nothing else on the card:
   * the muscle chip and the thumbnail degrade to absent, which is the honest answer for a fact the
   * read has not supplied, but a module that appears late moves every card beside it.
   */
  isAttributionPending: boolean,
): DayCardView {
  const sets = t(`${KEY}.sets`, { count: card.countingSetCount });
  // The name the header draws, and the one the grip and the menu refer to.
  const title = dayDisplayName(t, card.title, move.position);
  // Every exercise the day holds lands in either the warm-up block or a main-work position, so
  // "neither" is exactly `day.exercises.length === 0` — the predicate `phaseToInput` drops on.
  // Asked of the CARD rather than threaded down from the projection, because the card already
  // carries both halves and a second channel for the same fact is a second thing to keep in sync.
  const isEmpty = card.warmup === null && card.blocks.length === 0;
  return {
    key: card.slotToken,
    slotToken: card.slotToken,
    // A session the trainer never named still has to be identifiable, and its column head already
    // states where it falls — so the fallback names the POSITION, which is the coordinate its move
    // menu speaks in.
    title,
    authoredTitle: card.title,
    nameFieldLabel: t(`${KEY}.sessionNameLabel`),
    nameMaxLength: TEXT_LIMITS.sessionName.max,
    // The two counts, in the one sentence the day dock's header already says them in — the same
    // fact about the same day, so one key rather than a second copy of "{{exercises}} · {{sets}}".
    // The duration is NOT here any more; the weekday column above the card prints it.
    meta: t(`${KEY}.day.counts`, {
      exercises: t(`${KEY}.exercises`, { count: card.mainExerciseCount }),
      sets,
    }),
    warmup:
      card.warmup === null
        ? null
        : {
            title: t(`${KEY}.warmup`),
            badge: t(`${KEY}.warmupBadge`),
            rows: card.warmup.exercises.map((exercise) =>
              toExerciseRowView(t, exercise, lookUpExercise),
            ),
          },
    mainTitle: t(`${KEY}.mainWork`),
    blocks: card.blocks.map((block) => toBlockView(t, block, lookUpExercise)),
    // Three states, and the order is the whole point. While the catalogue read is OPEN the module
    // reserves its box and waits — it used to be absent and then appear, which grew every card on
    // the week at the moment the answer landed. Once the read has ANSWERED, a day whose exercises it
    // could not attribute gets NO module rather than an empty one: a "Serie / partia" heading over
    // nothing reads as a failure to load, and an absent module reads as what it is.
    load: isAttributionPending
      ? {
          title: t(`${KEY}.loadTitle`),
          rows: [],
          isPending: true,
          reservedRows: RESERVED_LOAD_ROWS,
          // The rail's own sentence, not a second copy of it: this module and those six cells are
          // one read and one wait, so they say the same thing.
          pendingLabel: t(`${KEY}.volume.pending`),
        }
      : load === undefined || load.entries.length === 0
        ? null
        : {
            title: t(`${KEY}.loadTitle`),
            rows: toBars(t, locale, load),
            isPending: false,
            reservedRows: RESERVED_LOAD_ROWS,
            pendingLabel: null,
          },
    programLabel: t(`${KEY}.programDay`),
    renameLabel: t(`${KEY}.day.renameAction`),
    headerActions: {
      duplicate: t(`${KEY}.dayActions.duplicateOn`, { name: title }),
      delete: t(`${KEY}.dayActions.deleteOn`, { name: title }),
    },
    unsavedNote: isEmpty ? t(`${KEY}.dayUnsaved`) : null,
    // The invitation a day with nothing in it draws instead of an empty box. Same condition as
    // `unsavedNote`, which states the CONSEQUENCE to a screen reader; this one states the ACT, and
    // the card body is the only place a trainer is looking when they meet it.
    emptyInvitation: isEmpty ? t(`${KEY}.emptyDay`) : null,
    move: {
      index: move.index,
      liftEntryLabel: t(`${KEY}.dnd.dayLiftEntry`),
      menuLabel: t(`${KEY}.dnd.dayMenu`, { name: title }),
      menuGroupLabel: move.menuGroupLabel,
      liftedLabel: move.liftedLabel(title),
      targets: move.targets,
      // One stop is the card's own, so a move has nowhere to land. Asked of the TARGETS rather
      // than of the canvas, because it is the same question `useKeyboardMove` refuses on and a
      // second derivation of it is how the two come to disagree.
      disabledReason: move.targets.length <= 1 ? t(`${KEY}.dnd.noOtherPosition`) : null,
    },
  };
}
